// File: src/components/ChatWindow.jsx
// FIXED: Use /status endpoint for periodic checks (no full refresh), increased interval to 30s

import React, { useState, useEffect, useRef } from 'react';
import { chatAPI } from '../services/api';
import './css/ChatWindow.css';

const ChatWindow = ({ 
  convoId, 
  socket, 
  user, 
  conversationStatus, 
  isConnected,
  onClose, 
  onNewConvo, 
  onConversationClosed 
}) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [localStatus, setLocalStatus] = useState(conversationStatus || 'open');
  const [showClosedMessage, setShowClosedMessage] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);
  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const statusCheckInterval = useRef(null);

  useEffect(() => {
    fetchMessages();
    socket.emit('join-convo', convoId);
    socket.emit('mark-read', { convoId });

    // Check status every 30s (less frequent, no full reload)
    statusCheckInterval.current = setInterval(checkStatus, 30000);

    return () => {
      if (statusCheckInterval.current) {
        clearInterval(statusCheckInterval.current);
      }
      socket.emit('leave-convo', convoId);
    };
  }, [convoId]);

  useEffect(() => {
    const handleNewMessage = (message) => {
      setMessages((prev) => {
        // Replace temp message with real one
        if (message.id && typeof message.id === 'number') {
          const withoutTemp = prev.filter(m => !m.id.toString().startsWith('temp_'));
          if (!withoutTemp.some(m => m.id === message.id)) {
            return [...withoutTemp, message];
          }
          return withoutTemp;
        }
        
        // Add temp message
        if (!prev.some(m => m.id === message.id)) {
          return [...prev, message];
        }
        return prev;
      });
      
      scrollToBottom();
      
      if (message.senderType === 'admin') {
        socket.emit('mark-read', { convoId });
      }
    };

    const handleMessagePersisted = ({ tempId, realId, message }) => {
      setMessages(prev => {
        const filtered = prev.filter(m => m.id !== tempId);
        if (!filtered.some(m => m.id === realId)) {
          return [...filtered, message];
        }
        return filtered;
      });
      
      setSendingMessage(false);
    };

    const handleMessageError = ({ tempId, message: errorMsg }) => {
      console.error('❌ Message error:', errorMsg);
      
      setMessages(prev => prev.filter(m => m.id !== tempId));
      setSendingMessage(false);
      alert(`Echec d'envoi du message : ${errorMsg}`);
    };

    const handleTyping = (data) => {
      const myType = user?.isAdmin ? 'admin' : 'user';
      if (data.senderType !== myType) {
        setIsTyping(data.isTyping);
      }
    };

    const handleClosed = (data) => {
      setLocalStatus('closed');
      setShowClosedMessage(true);
      
      setTimeout(() => {
        onConversationClosed();
      }, 5000);
    };

    const handleReopened = () => {
      setLocalStatus('open');
      setShowClosedMessage(false);
    };

    socket.on('new-message', handleNewMessage);
    socket.on('message-persisted', handleMessagePersisted);
    socket.on('message-error', handleMessageError);
    socket.on('user-typing', handleTyping);
    socket.on('convo-closed', handleClosed);
    socket.on('convo-reopened', handleReopened);

    return () => {
      socket.off('new-message', handleNewMessage);
      socket.off('message-persisted', handleMessagePersisted);
      socket.off('message-error', handleMessageError);
      socket.off('user-typing', handleTyping);
      socket.off('convo-closed', handleClosed);
      socket.off('convo-reopened', handleReopened);
    };
  }, [socket, convoId, user]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    setLocalStatus(conversationStatus);
    if (conversationStatus === 'closed') {
      setShowClosedMessage(true);
    }
  }, [conversationStatus]);

  /* -------------------------------------------------
     Lightweight status check (no messages reload)
     ------------------------------------------------- */
  const checkStatus = async () => {
    try {
      const guestToken = localStorage.getItem('chat_guest_token');
      const headers = {};
      
      if (guestToken && !user) {
        headers['x-guest-token'] = guestToken;
      }

      const { data } = await chatAPI.getConversationStatus(convoId, headers); // New lightweight endpoint
      
      if (data.status !== localStatus) {
        setLocalStatus(data.status);
        setShowClosedMessage(data.status === 'closed');
      }
      
      // Optional: Update unread if changed (but don't reload messages)
      // setUnreadCount(data.unreadCount); // If you add unread state
      
    } catch (err) {
      console.error('Status check failed:', err);
      
      if (err.response?.status === 404) {
        setError("Cette conversation n'est plus disponible.");
        setTimeout(() => onNewConvo('closed'), 2000);
      }
    }
  };

  const fetchMessages = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const guestToken = localStorage.getItem('chat_guest_token');
      const headers = {};
      
      if (guestToken && !user) {
        headers['x-guest-token'] = guestToken;
      }

      const { data } = await chatAPI.getConversation(convoId, headers);
      setMessages(data.messages || []);
      setLocalStatus(data.conversation.status);
      
      if (data.conversation.status === 'closed') {
        setShowClosedMessage(true);
      }
    } catch (err) {
      console.error('Failed to fetch messages:', err);
      
      if (err.response?.status === 404) {
        setError('Conversation introuvable. Demarrage d\'une nouvelle conversation...');
        setTimeout(() => onNewConvo('restart'), 2000);
      } else if (err.response?.status === 403) {
        setError('Acces refuse. Veuillez demarrer une nouvelle conversation.');
        setTimeout(() => onNewConvo('closed'), 2000);
      } else {
        setError('Impossible de charger les messages. Veuillez actualiser.');
      }
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = () => {
    const content = input.trim();
    if (!content || localStatus !== 'open') {
      alert('Conversation fermee. Demarrez-en une nouvelle pour envoyer un message.');
      return;
    }

    if (!isConnected) {
      alert('Connexion perdue. Veuillez patienter et reessayer.');
      return;
    }

    setSendingMessage(true);
    
    socket.emit('send-message', {
      convoId,
      content,
      attachmentUrl: null,
    });

    setInput('');
    socket.emit('typing', { convoId, isTyping: false });
    
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    // Reset sending state after timeout as fallback
    setTimeout(() => {
      setSendingMessage(false);
    }, 5000);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    } else if (localStatus === 'open' && isConnected) {
      socket.emit('typing', { convoId, isTyping: true });
      
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      
      typingTimeoutRef.current = setTimeout(() => {
        socket.emit('typing', { convoId, isTyping: false });
      }, 1000);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const getSenderName = (msg) => {
    if (msg.senderType === 'admin') return 'Assistance';
    if (user) return user.firstName || 'Vous';
    return 'Vous';
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return "A l'instant";
    if (diff < 3600000) return `il y a ${Math.floor(diff / 60000)} min`;
    
    return date.toLocaleTimeString('fr-FR', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const handleStartNewConversation = () => {
    onNewConvo('restart');
  };

  if (loading) {
    return (
      <div className="chat-window">
        <div className="chat-header">
          <h3>Chargement du chat...</h3>
          <button onClick={onClose}>×</button>
        </div>
        <div className="chat-loading">
          <div className="spinner"></div>
          <p>Chargement de votre conversation...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="chat-window">
        <div className="chat-header">
          <h3>Erreur</h3>
          <button onClick={onClose}>×</button>
        </div>
        <div className="chat-error">
          <p>{error}</p>
          <button onClick={fetchMessages} className="btn-retry">Reessayer</button>
        </div>
      </div>
    );
  }

  return (
    <div className="chat-window">
      <div className="chat-header">
        <div className="chat-header-info">
          <h3>Chat assistance</h3>
          <p className="chat-status">
            {user ? `${user.firstName}` : 'Invite'}
            {!isConnected && <span className="status-badge disconnected">Hors ligne</span>}
            {localStatus === 'closed' && <span className="status-badge closed">Fermee</span>}
            {localStatus === 'open' && isConnected && <span className="status-badge open">En ligne</span>}
          </p>
        </div>
        <button className="chat-close-btn" onClick={onClose}>×</button>
      </div>

      {showClosedMessage && (
        <div className="chat-closed-banner">
          <p>⚠️ Cette conversation a ete fermee par l'assistance.</p>
          <button onClick={handleStartNewConversation} className="btn-new-chat">
            Demarrer une nouvelle conversation
          </button>
        </div>
      )}

      {!isConnected && (
        <div className="chat-connection-banner">
          <p>⚠️ Connexion perdue. Reconnexion...</p>
        </div>
      )}

      <div className="chat-messages">
        {messages.length === 0 ? (
          <div className="chat-empty-state">
            <p>👋 Bienvenue ! Comment pouvons-nous vous aider aujourd'hui ?</p>
          </div>
        ) : (
          messages.map((msg) => (
            <div 
              key={msg.id} 
              className={`message ${msg.senderType === 'admin' ? 'admin-msg' : 'user-msg'} ${
                msg.id.toString().startsWith('temp_') ? 'message-sending' : ''
              }`}
            >
              <div className="message-content">
                <div className="message-header">
                  <strong>{getSenderName(msg)}</strong>
                  <span className="message-time">
                    {formatTime(msg.timestamp)}
                    {msg.id.toString().startsWith('temp_') && ' (Envoi...)'}
                  </span>
                </div>
                <p>{msg.content}</p>
                {msg.attachmentUrl && (
                  <img 
                    src={msg.attachmentUrl} 
                    alt="Piece jointe" 
                    className="message-attachment"
                    loading="lazy"
                    decoding="async"
                  />
                )}
              </div>
            </div>
          ))
        )}
        
        {isTyping && localStatus === 'open' && (
          <div className="typing-indicator">
            <span></span>
            <span></span>
            <span></span>
            <p>L'assistance ecrit...</p>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      <div className="chat-input">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={
            !isConnected ? 'Connexion...' :
            localStatus === 'closed' ? 'Conversation fermee' : 
            'Tapez votre message...'
          }
          disabled={loading || localStatus === 'closed' || !isConnected || sendingMessage}
        />
        <button 
          onClick={sendMessage}
          disabled={!input.trim() || loading || localStatus === 'closed' || !isConnected || sendingMessage}
          className="send-btn"
        >
          {sendingMessage ? 'Envoi...' : 
           !isConnected ? 'Hors ligne' :
           localStatus === 'closed' ? 'Fermee' : 'Envoyer'}
        </button>
      </div>

      {localStatus === 'closed' && (
        <div className="chat-footer-action">
          <button onClick={handleStartNewConversation} className="btn-new-conversation">
            Demarrer une nouvelle conversation
          </button>
        </div>
      )}
    </div>
  );
};

export default ChatWindow;
